import { computed, effectScope, ref, watch } from 'vue'
import type { RouteLocationNormalized } from 'vue-router'
import { START_LOCATION } from 'vue-router'

import i18n from '@/libs/i18n'
import { moveFavourite as moveInList, parseFavourites, serialiseFavourites } from '@/libs/navFavourites'
import { appNavGroups, NAV_TAB_GROUPS, NESTED_ROUTES, SETTINGS_ROUTE_NAMES, settingsNavGroups } from '@/libs/navManifest'
import { listMyApprovals } from '@/libs/stockRequisition'
import { PrintWarning } from '@/libs/toast'
import router, { openingRoute } from '@/router'
import { useAppStore } from '@/stores/app'
import { useFetchStore } from '@/stores/fetch'
import type { INavGroup, INavItem, INavTabGroup, NavTabGroupKey } from '@/types/Nav'
import type { ITelemetryDemoConsole } from '@/types/Telemetry'

/**
 * The one place that decides what this account may see in the menu.
 *
 * # Why it is a singleton in a detached scope
 *
 * Three screens read it — the sidebar, the Ctrl+K palette and the settings hub — and
 * the sidebar draws about eighty entries, every one of them a component that asks
 * whether it is active and whether it is pinned.
 *
 * Built per caller, that would be eighty copies of the whole filtered manifest and
 * eighty `router.resolve` sweeps over seventy-nine entries on every navigation. Worse,
 * the WATCHERS would belong to whichever component happened to ask first — Default.vue
 * — and App.vue swaps the layout away on the login screen and on a printable document,
 * so the first sign-out would take the watchers with it and nothing would register
 * them again: the badges would freeze, and the next person at the same desk would
 * inherit the previous one's favourites in memory and save them under their own name.
 *
 * So everything reactive is created ONCE, inside a scope that belongs to no component
 * (`effectScope(true)`), on the first call. It reads the router instance and the i18n
 * instance directly rather than through `useRoute`/`useI18n`, which need a component
 * to be set up around them; the two stores are Pinia's own singletons and are read the
 * ordinary way.
 */

/** Up to this many favourites. A ninth is refused with a word rather than silently. */
const FAVOURITES_MAX = 8

/** How many are offered to somebody who has never pinned one. */
const FAVOURITES_DEFAULT_COUNT = 6

/**
 * What a new account gets, in priority order, until six of them are VISIBLE to it.
 *
 * Not "the first six entries of the menu": a storekeeper's first six would be the
 * reception spine, which they may not even open. The list is read down and whatever
 * this account can actually see is taken, so a book-keeper lands on the invoices and a
 * housekeeper on the floor.
 */
const FAVOURITE_DEFAULTS = [
    'chessboard', 'reservations', 'availability', 'guests',
    'materials-overview', 'stock', 'materials-receiving-issues',
    'staff-today', 'staff-me', 'housekeeping-overview', 'finance', 'reports',
    // „Poročanje AJPES“ is LAST, and that is a decision rather than where it happened to
    // be appended (14. 9. 2026). The list is one global priority order and six of it are
    // taken, so a place higher up does not ADD a favourite — it removes the sixth from
    // every account that can see this screen: Finance from a receptionist, Gostje from an
    // administrator. The screen is already the second thing in the Recepcija fold and has
    // a card of its own on „Danes“, which is a stronger place than a pinned row, and
    // anybody who wants it pinned presses the star.
    //
    // Down here it is still worth listing: an account whose whole working menu is guest
    // reporting — a compliance clerk with `compliance:read` and little else — fills fewer
    // than six from the names above and lands on this one.
    'registrations',
]

const OPEN_GROUP_KEY  = 'pms:nav-open-group'
const CLASSIC_NAV_KEY = 'pms:nav-classic'
const FAVOURITES_KEY  = 'pms:nav-favourites'

/**
 * localStorage that cannot throw.
 *
 * A browser with site data switched off throws on the read rather than answering null,
 * and a sidebar that will not render is a worse failure than one that forgot which
 * fold was open.
 */
export const readText = (key: string): string => {
    try {
        return localStorage.getItem(key) ?? ''
    } catch {
        return ''
    }
}

export const writeText = (key: string, value: string) => {
    try {
        localStorage.setItem(key, value)
    } catch {
        // A browser that will not remember the preference still works; it simply
        // starts from the default next time.
    }
}

const removeText = (key: string) => {
    try {
        localStorage.removeItem(key)
    } catch {
        // Same bargain as above.
    }
}

/** Diacritics off and lower-cased, so „Prejeti računi“ is found by typing "racuni". */
export const foldAccents = (text: string): string =>
    text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()

const createNav = () => {
    const appStore   = useAppStore()
    const fetchStore = useFetchStore()

    /**
     * The screen the menu answers about, so this needs no component around it.
     *
     * The router's own current route — except while its FIRST navigation is still being
     * decided, when there is no current route yet and the one it is going to is the
     * honest answer. See `openingRoute` in the router for what that wait costs.
     */
    const route = computed((): RouteLocationNormalized => {
        const current = router.currentRoute.value

        return current === START_LOCATION ? openingRoute.value ?? current : current
    })

    /** The i18n instance rather than `useI18n()`, for the same reason. */
    const t = (key: string): string => i18n.global.t(key)

    // -- The list, filtered ------------------------------------------------------

    /**
     * A menu entry whose route has not landed, or that this account may not open, is
     * filtered out — a link that 403s teaches the desk the system is unreliable.
     *
     * Filtered against the router as well: an entry whose route does not exist yet
     * would make RouterLink throw on render, so a screen that has not landed simply
     * stays out of the menu until its route does. An EXTERNAL door has no route of
     * this portal to find and is exempt from that one test alone.
     */
    const allow = (list: INavItem[]): INavItem[] => list
        .filter(item => item.external || router.hasRoute(item.name))
        .filter(item => item.when !== false)
        .filter(item => !item.module || appStore.hasModule(item.module))
        .filter(item => (item.modules ?? []).every(key => appStore.hasModule(key)))
        .filter(item => !item.tenantWide || appStore.everywhere)
        .filter(item => (item.allOf ?? []).every(permission => appStore.can(permission)))
        .filter(item => item.anyOf
            ? item.anyOf.some(permission => appStore.can(permission))
            : (!item.permission || appStore.can(item.permission)))

    /**
     * The manifest with `allow` applied to every entry, and with everything empty
     * dropped: a section whose entries were all filtered out draws no heading, and a
     * group with nothing left in it is not drawn at all.
     */
    const filterGroups = (groups: INavGroup[]): INavGroup[] => groups
        .map(group => ({
            ...group,
            sections: group.sections
                .map(section => ({ ...section, items: allow(section.items) }))
                .filter(section => section.items.length),
        }))
        .filter(group => group.sections.length)

    /** How many requisitions wait for this account; see `loadPendingApprovals`. */
    const pendingApprovals = ref(0)

    const context = computed(() => ({
        t,
        pendingApprovals: pendingApprovals.value,
        demoConsole:      appStore.demoConsole,
    }))

    /**
     * The working menu with every screen still standing on its own.
     *
     * This is the list the PALETTE and the favourites read: the collapsing below is a
     * fact about the sidebar, and somebody who types „napoved“ into Ctrl+K is asking
     * for a screen rather than for the tab it became.
     */
    const searchGroups = computed((): INavGroup[] => filterGroups(appNavGroups(context.value)))

    /** What lives behind the gear — what a house decides once. Nothing here is tabbed. */
    const settingsGroups = computed((): INavGroup[] => filterGroups(settingsNavGroups(context.value)))

    /** Every visible entry of both menus, which is what the palette searches. */
    const allItems = computed((): INavItem[] =>
        [...searchGroups.value, ...settingsGroups.value]
            .flatMap(group => group.sections.flatMap(section => section.items)))

    // -- Tab groups --------------------------------------------------------------

    /**
     * The VISIBLE members of every tab group, in the manifest's order.
     *
     * The order is the tab order, and the first of them is where the menu entry leads
     * — which is why it is taken from the filtered list and not from the manifest: a
     * buyer with no `stock:read` opens „Nabava“ on the orders, and a storekeeper who
     * may only read the codebooks opens the same entry on the suppliers.
     */
    const tabMembers = computed((): Partial<Record<NavTabGroupKey, INavItem[]>> => {
        const members: Partial<Record<NavTabGroupKey, INavItem[]>> = {}

        for (const item of allItems.value) {
            if (!item.tabs) continue

            members[item.tabs] = [...members[item.tabs] ?? [], item]
        }

        return members
    })

    const tabGroup = (key: NavTabGroupKey): INavTabGroup | undefined =>
        NAV_TAB_GROUPS.find(group => group.key === key)

    /** What the menu calls a whole group, for the entry and for the palette's suffix. */
    const tabGroupLabel = (key: NavTabGroupKey): string => {
        const group = tabGroup(key)

        return group ? t(group.labelKey) : ''
    }

    /** Which group a screen is a tab of, or null for the screens that are not tabs. */
    const tabGroupOf = (name: string): NavTabGroupKey | null =>
        allItems.value.find(item => item.name === name)?.tabs ?? null

    /** The visible members of a group, or nothing at all when there is no such group. */
    const tabGroupMembers = (key: NavTabGroupKey | null): INavItem[] =>
        (key && tabMembers.value[key]) || []

    /**
     * The ONE entry that stands for a whole tab group.
     *
     * It borrows the first visible member's route and icon — an entry has to lead
     * somewhere, and the group has no screen of its own — and takes the group's name
     * for its label. The badge is the members' badges ADDED UP, which is the whole
     * reason this is derived rather than written down: „Moje odobritve“ is no longer
     * an entry of the menu, and the count of what is waiting for an approver has to
     * survive that or the badge was pointless.
     *
     * `groupEntry` is what tells it apart from an entry leading to that first screen and
     * nothing else, which its `name` cannot: it lights for any of its tabs, and it
     * carries no pin — see `INavItem.groupEntry`.
     */
    const tabGroupEntry = (key: NavTabGroupKey): INavItem | null => {
        const members = tabGroupMembers(key)
        const first   = members[0]
        const group   = tabGroup(key)

        if (!first || !group) return null

        const badge = members.reduce((sum, member) => sum + (member.badge ?? 0), 0)

        return {
            ...first,
            label:      t(group.labelKey),
            badge:      badge || undefined,
            tabs:       key,
            groupEntry: true,
        }
    }

    /**
     * A section's entries with each tab group replaced, in place, by its one entry.
     *
     * In place, because the position of the group in the fold is the position of its
     * first member — „Nabava“ stands where the orders stood, between the stocktakes
     * above it and „Prevzem“ below.
     */
    const collapseTabs = (items: INavItem[]): INavItem[] => {
        const done: NavTabGroupKey[] = []

        return items.flatMap((item): INavItem[] => {
            if (!item.tabs) return [item]
            if (done.includes(item.tabs)) return []

            done.push(item.tabs)

            const entry = tabGroupEntry(item.tabs)

            // Fails OPEN: a screen tagged with a group that does not exist keeps its own
            // entry rather than vanishing from the menu. The type makes that unreachable
            // today, and it is written this way because the failure it guards against is
            // silent — a screen nobody can find, and nothing anywhere saying so.
            return entry ? [entry] : [item]
        })
    }

    /** The working menu — what a desk opens during a shift, tab groups folded into one. */
    const groups = computed((): INavGroup[] => searchGroups.value.map(group => ({
        ...group,
        sections: group.sections.map(section => ({ ...section, items: collapseTabs(section.items) })),
    })))

    // -- Which entry the screen belongs to ---------------------------------------

    /**
     * Every visible entry's own address, resolved once per change of the menu.
     *
     * Through the router rather than guessed from the route name: half the names here
     * do not match their path at all („reminders“ is /opomniki, „dining“ is /obroki),
     * which is why the menu this replaces highlighted nothing for them.
     */
    const itemPaths = computed((): { name: string, path: string }[] => allItems.value
        .filter(item => !item.external)
        .map(item => {
            try {
                return { name: item.name, path: router.resolve({ name: item.name }).path }
            } catch {
                return { name: item.name, path: '' }
            }
        })
        .filter(entry => !!entry.path))

    /**
     * The entry whose address is the LONGEST prefix of the screen being read.
     *
     * The longest and not merely a match, because the addresses nest: /materials is
     * „Pregled“ and /materials/orders is „Naročilnice“, and a plain prefix test would
     * light both. One winner per screen, so a detail page under /dogodki/{id} or
     * /materials/orders/{id} lights the register it came from.
     */
    const prefixMatch = computed((): string => {
        let best = ''
        let bestPath = ''

        for (const { name, path } of itemPaths.value) {
            if (route.value.path !== path && !route.value.path.startsWith(`${path}/`)) continue
            if (path.length <= bestPath.length) continue

            best     = name
            bestPath = path
        }

        return best
    })

    /** The entry the screen being read belongs to, by name, address or nesting. */
    const activeName = computed((): string => {
        const name = String(route.value.name ?? '')

        if (NESTED_ROUTES[name]) return NESTED_ROUTES[name]
        if (allItems.value.some(item => item.name === name)) return name

        return prefixMatch.value
    })

    /**
     * Whether an entry of the MENU is the screen being read.
     *
     * It takes the ENTRY and not its name, because the name is not enough: the derived
     * entry of a tab group borrows its first member's route, so „Nabava“ and a pinned
     * „Naročilnice“ are the same string and must answer differently.
     *
     *   - The derived entry lights for ANY of its tabs. Otherwise it would go dark the
     *     moment somebody pressed „Zahtevki“, and the menu would be answering "you are
     *     nowhere", which is the one thing a menu must never do.
     *   - Every other entry — a plain one, a favourite, a settings entry — is the exact
     *     screen or it is not. A pinned „Prejeti računi“ lighting up while the screen is
     *     „Knjigovodstvo“ would be the menu pointing at the wrong place.
     *
     * The tab ROW does not use this either — it compares `activeName` itself, because
     * there exactly one tab is the screen and the first one is not special.
     */
    const isEntryActive = (item: INavItem): boolean => {
        if (item.external) return false
        if (activeName.value === item.name) return true
        if (!item.groupEntry) return false

        return tabGroupOf(activeName.value) === item.tabs
    }

    /**
     * The tab row above the page, or null on a screen that is nobody's tab.
     *
     * Read from the screen being read rather than from the entry that was pressed, so
     * the row is right after a reload, after a bookmark and after Milo has sent
     * somebody to one of these screens by name.
     */
    const activeTabs = computed((): { group: INavTabGroup, items: INavItem[] } | null => {
        const key   = tabGroupOf(activeName.value)
        const group = key ? tabGroup(key) : undefined
        const items = tabGroupMembers(key)

        // More than one, because a row holding a single tab is a line of screen that
        // offers nothing: it is the page you are already on. It happens for real — the
        // chain group is two screens on two different rights, and plenty of accounts
        // hold one of them.
        return group && items.length > 1 ? { group, items } : null
    })

    /**
     * Whether the screen being read belongs behind the gear.
     *
     * Decided by the ENTRY and not by the address, because two settings screens do not
     * live under /settings: the register of houses is /properties and the approval
     * ladder is /materials/approval-rules. On a path test, opening either from the gear
     * threw the sidebar back to the working menu — which does not contain them — and
     * recorded /properties as the screen to go "back" to.
     *
     * The hub itself is the one thing decided by the address: /settings is a screen of
     * this group without being an entry in it.
     */
    const inSettings = computed((): boolean =>
        route.value.path.startsWith('/settings') || SETTINGS_ROUTE_NAMES.has(activeName.value))

    // -- Favourites --------------------------------------------------------------

    /**
     * Where this account's favourites are kept.
     *
     * Named by the account rather than shared by the browser: two receptionists share
     * the machine at the desk, and a pinned „Moje odobritve“ is a statement about a
     * person's work rather than about the computer. It is still `localStorage`, so it
     * is that account's list IN THIS BROWSER — signing in on another machine starts
     * from the defaults. An install that has not read the account yet falls back to the
     * login, which is what the sign-in screen knew.
     */
    const favouritesStorageKey = computed((): string => {
        const who = appStore.userData?.id || appStore.userData?.username || appStore.userData?.email || 'anon'

        return `${FAVOURITES_KEY}:${who}`
    })

    /**
     * The pinned route names, or null while nobody has pinned anything — which is not
     * the same as an empty list. Null takes the defaults; an empty list is a person who
     * deliberately unpinned the lot.
     */
    const favourites = ref<string[] | null>(null)

    const readFavourites = (key: string): string[] | null => parseFavourites(readText(key))

    /** What a person who has pinned nothing is given — see FAVOURITE_DEFAULTS. */
    const defaultFavourites = computed((): string[] => {
        // Tab members are deliberately left out: a screen that the menu no longer draws
        // on its own is not a thing to OFFER somebody who has pinned nothing. They can
        // still be pinned one at a time from the palette, which is a person saying they
        // want that particular screen.
        const visible = new Set(allItems.value.filter(item => !item.tabs).map(item => item.name))

        return FAVOURITE_DEFAULTS.filter(name => visible.has(name)).slice(0, FAVOURITES_DEFAULT_COUNT)
    })

    const favouriteNames = computed((): string[] => favourites.value ?? defaultFavourites.value)

    /**
     * The pinned entries, in the order they were pinned, minus any this account can no
     * longer see: a permission withdrawn or a module switched off must take the
     * favourite with it, not leave a door that answers 403.
     */
    const favouriteItems = computed((): INavItem[] => favouriteNames.value
        .map(name => allItems.value.find(item => item.name === name))
        .filter((item): item is INavItem => !!item))

    const isFavourite = (name: string): boolean => favouriteNames.value.includes(name)

    const saveFavourites = (names: string[]) => {
        favourites.value = names
        writeText(favouritesStorageKey.value, serialiseFavourites(names))
    }

    const toggleFavourite = (name: string) => {
        const current = [...favouriteNames.value]
        const at      = current.indexOf(name)

        if (at >= 0) {
            current.splice(at, 1)
            saveFavourites(current)

            return
        }

        if (current.length >= FAVOURITES_MAX) {
            PrintWarning(i18n.global.t('nav.favourites_max', { count: FAVOURITES_MAX }))

            return
        }

        current.push(name)
        saveFavourites(current)
    }

    /**
     * One favourite moved to just before another — or to the end, when `before` is null.
     *
     * The order is somebody's own arrangement of their own menu, so it is saved the
     * moment it changes: a list that had to be confirmed would be a dialog on top of a
     * drag, and there is nothing to confirm — the next drag is the undo.
     *
     * Moving one of the DEFAULTS writes the defaults down as they now stand, which is
     * what makes the first drag stick. „Ponastavi“ is the way back out of that, and it
     * still is: it removes the key rather than writing a list.
     */
    const moveFavourite = (name: string, before: string | null) => {
        const next = moveInList(favouriteNames.value, name, before)

        // The same reference back means the move moved nothing — a drop where the entry
        // already was — and writing that would be a save with no change in it.
        if (next === favouriteNames.value) return

        saveFavourites(next)
    }

    /** Back to what a new account is given, in the order the role is given them in. */
    const resetFavourites = () => {
        favourites.value = null
        removeText(favouritesStorageKey.value)
    }

    // -- The two facts a permission cannot answer --------------------------------

    /**
     * How many requisitions are waiting for THIS account to answer
     * (docs/MATERIALNO-ZAHTEVKI.md, WP-M4).
     *
     * Read once when the menu is drawn, and again only when the house or the
     * permissions change — never polled. A number in the corner of the eye that moves
     * on its own is a notification, and this portal has a bell for those.
     *
     * A failed call leaves the badge off, which is the safe way round: the worst case
     * is an approver who opens the screen anyway.
     */
    const loadPendingApprovals = async () => {
        if (!appStore.can('materials:approve') || !appStore.hasModule('materials') || !appStore.propertyId) {
            pendingApprovals.value = 0

            return
        }

        const { ok, requisitions } = await listMyApprovals(appStore.propertyId)

        pendingApprovals.value = ok ? requisitions.length : 0
    }

    /**
     * Whether this house has a demonstration console, asked once per house.
     *
     * The service answers with an empty scenario list unless its driver is the
     * simulator AND this house has it switched on, so one call decides the entry. It is
     * asked only where the account could open the screen anyway — a receptionist's
     * browser must not be sending requests to an endpoint that would refuse it.
     *
     * A failed call leaves the entry out, which is the safe way round: the worst case
     * is a presenter who has to reload, not a hotel with a button that opens windows.
     */
    const loadDemoConsole = async () => {
        if (!appStore.can('telemetry:automation') || !appStore.propertyId) {
            appStore.setDemoConsole(false)

            return
        }

        const { statusCode, data } = await fetchStore
            .fetch(`/properties/${appStore.propertyId}/telemetry/demo`)
            .get()
            .json<ITelemetryDemoConsole>()

        appStore.setDemoConsole(statusCode.value === 200 && (data.value?.scenarios?.length ?? 0) > 0)
    }

    // The permissions arrive after the menu is first drawn — a reload does not go
    // through the login screen, so the account is read in App.vue — and the house can
    // be switched at any time. Both are watched, because the answer depends on both.
    watch([() => appStore.propertyId, () => appStore.permissions], () => {
        void loadDemoConsole()
        void loadPendingApprovals()
    }, { immediate: true })

    // A change of account re-reads the list. It is what keeps the second receptionist
    // at the same desk from inheriting the first one's pins — and from saving them
    // under their own name.
    watch(favouritesStorageKey, key => { favourites.value = readFavourites(key) }, { immediate: true })

    /** The last screen that was not a settings screen, for „Nazaj v aplikacijo“. */
    const lastAppPath = ref('')

    // Remembered so that „Nazaj v aplikacijo“ puts somebody back where they were rather
    // than on the chessboard. The address and not the route name, because a reservation
    // that was open is a reservation, not „rezervacije“.
    watch(() => route.value.fullPath, path => {
        if (!inSettings.value) lastAppPath.value = path
    }, { immediate: true })

    // -- What is open ------------------------------------------------------------

    /** Whether the palette is open. Opened by ⌘/Ctrl+K and by the search entry. */
    const searchOpen = ref(false)

    /**
     * Whether this browser asked for the menu it had before.
     *
     * A ref and not a read of `localStorage` per render, so the switch on the Videz
     * screen takes effect on the sidebar that is already drawn, without a reload.
     */
    const classicNav = ref(readText(CLASSIC_NAV_KEY) === '1')

    return {
        groups, searchGroups, settingsGroups, isEntryActive, inSettings,
        activeName, activeTabs, tabGroupLabel,
        favouriteItems, isFavourite, toggleFavourite, moveFavourite, resetFavourites,
        searchOpen,
        openSearch:  () => { searchOpen.value = true },
        closeSearch: () => { searchOpen.value = false },
        classicNav,
        setClassicNav: (on: boolean) => {
            classicNav.value = on
            writeText(CLASSIC_NAV_KEY, on ? '1' : '0')
        },
        lastAppPath,
        openGroupKey: OPEN_GROUP_KEY,
    }
}

/** The scope the state above lives in. Detached: it outlives every component. */
const scope = effectScope(true)

let shared: ReturnType<typeof createNav> | null = null

/**
 * The menu. Every caller gets the same one — see the note at the top of this file.
 *
 * It is built on the first call rather than at import time because it reads two Pinia
 * stores, and Pinia is installed while the application is being created.
 */
export const useNav = () => {
    shared ??= scope.run(createNav) as ReturnType<typeof createNav>

    return shared
}
